import {
  automationArtifactIdSchema,
  type AutomationArtifactId,
} from "@repo/app-types/entities/automationArtifact";
import { REPORT_DELEGATE_TOOL_ID } from "@repo/app-types/entities/reportOrchestration";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
  type ChatMessageRenderToolGroupArgs,
  type ChatToolComponents,
} from "@repo/ui/chat/chatMessage";
import {
  ChatToolBase,
  ChatToolGroup,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { ModelOutputContent } from "@repo/ui/structuredContent/modelOutputContent";
import type { UIMessage } from "ai";
import { useEffect, useMemo, type FC, type ReactNode } from "react";
import { Link } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { buildEntityResolversFromToolResults } from "../agents/toolResultEntityResolvers.js";
import { CountCustomerInteractionsTool } from "../agents/tools/countCustomerInteractionsTool.js";
import { FallbackTool } from "../agents/tools/fallbackTool.js";
import { GetCriterionDetailsTool } from "../agents/tools/getCriterionDetailsTool.js";
import { GetPlaybookTeamScoresTool } from "../agents/tools/getPlaybookTeamScoresTool.js";
import { GetTeamRankingsTool } from "../agents/tools/getTeamRankingsTool.js";
import { guardToolRender } from "../agents/tools/guardToolRender.js";
import { ResearchCustomerInteractionsTool } from "../agents/tools/researchCustomerInteractionsTool.js";
import { StructuredContentReferenceProviders } from "../structuredContent/structuredContentReferenceProviders.js";
import { AutomationReportTile } from "./automationReportTile.js";
import { useRunTranscriptContext } from "./runTranscriptContext.js";

/** The tool whose call saves an artifact; rendered as a tile linking to it. */
const SAVE_REPORT_TOOL_NAME = "save_report";

const assistantStyle = messageItemStyle({ role: "assistant" });

/**
 * Tools whose rows are the run's results — each manager's delegation and each
 * saved report — so their run never folds.
 */
const UNFOLDED_TOOL_NAMES: ReadonlySet<string> = new Set([
  SAVE_REPORT_TOOL_NAME,
  REPORT_DELEGATE_TOOL_ID,
]);

function renderRunToolGroup(args: ChatMessageRenderToolGroupArgs): ReactNode {
  const { tools, isActive, children } = args;
  if (tools.some((tool) => UNFOLDED_TOOL_NAMES.has(tool.toolName))) {
    return children;
  }
  return (
    <ChatToolGroup tools={tools} isActive={isActive}>
      {children}
    </ChatToolGroup>
  );
}

export interface RunTranscriptProps {
  messages: readonly UIMessage[];
  /**
   * True while the thread is still streaming in. Shows a typing indicator when
   * the agent is between visible parts (thinking before its first tool call,
   * or after a tool completed) so a live thread never looks stalled.
   */
  isStreaming?: boolean;
  /**
   * Tool renderers for this thread. Defaults to the research subagent's
   * registry (research tools + the save_report tile); the orchestrator thread
   * passes its own (see `orchestratorTranscript.tsx`).
   */
  tools?: ChatToolComponents;
}

/**
 * Renders an agent's conversation as a read-only chat thread using the shared
 * `ChatMessage` component. The generated prompt (a user message) is hidden —
 * only the agent's side is shown. By default the tool registry is the research
 * subagent's (research tools + a `save_report` tile linking to the saved
 * report); pass `tools` to render a different agent's conversation — the
 * orchestrator thread does, nesting each subagent's thread inside its
 * delegation tool call.
 *
 * Must render inside a `RunTranscriptProvider`: the tool components are static
 * module-level references (so streaming updates re-render rather than remount
 * them) and read the run's ambient values from that context.
 */
export const RunTranscript: FC<RunTranscriptProps> = (props) => {
  const { messages, isStreaming = false, tools: toolsOverride } = props;
  const tools = toolsOverride ?? RESEARCH_THREAD_TOOLS;

  // Quotes and references in the agent's messages cite interactions and
  // teammates returned by its tool calls, so resolve them against those tool
  // results.
  const { resolveInteractionId, resolveTeammatePlaybookId } = useMemo(
    () => buildEntityResolversFromToolResults(messages),
    [messages],
  );

  const renderTranscriptText = ({
    text,
    isStreaming: isTextStreaming,
  }: ChatMessageRenderTextArgs): ReactNode => (
    <div className={assistantStyle.row()}>
      <div className={assistantStyle.bubble()}>
        <ModelOutputContent
          text={text}
          isStreaming={isTextStreaming}
          resolveInteractionId={resolveInteractionId}
          context={{ resolveTeammatePlaybookId }}
        />
      </div>
    </div>
  );

  // The run is autonomous: its only user message is the generated prompt and
  // there is no system message. Show just the agent's conversation.
  const agentMessages = messages.filter(
    (message) => message.role === "assistant",
  );

  if (agentMessages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This run has no conversation yet.
      </p>
    );
  }

  return (
    <StructuredContentReferenceProviders>
      <div className="flex flex-col gap-4">
        {agentMessages.map((message, index) => (
          <div key={message.id} className="flex flex-col gap-3">
            <ChatMessage
              message={message}
              isStreaming={isStreaming && index === agentMessages.length - 1}
              renderText={renderTranscriptText}
              renderToolGroup={renderRunToolGroup}
              tools={tools}
            />
          </div>
        ))}
      </div>
    </StructuredContentReferenceProviders>
  );
};

/** The `save_report` renderer: a report tile linking to the artifact. */
const SaveReportTool: ToolComponent = ({ input, output, status }) => {
  const {
    playbookId,
    automationId,
    teammateNamesById,
    isRunTerminal,
    onArtifactSaved,
  } = useRunTranscriptContext();
  const title = getReportTitle(input) ?? "Report";
  const recipientId = getRecipientTeammateId(input);
  const recipientName = recipientId
    ? teammateNamesById.get(recipientId)
    : undefined;
  const savedArtifactId = getSavedArtifactId(output);

  // A report saved live is a new row this automation's results list doesn't have
  // yet, so announce it once (keyed on the id) for the app to refresh that
  // list. Skip while replaying a finished run's persisted transcript — the
  // artifact already existed, and the results view refetches on mount anyway.
  useEffect(() => {
    if (!isRunTerminal && savedArtifactId) {
      onArtifactSaved?.(savedArtifactId);
    }
  }, [isRunTerminal, savedArtifactId, onArtifactSaved]);

  // Still writing/saving — a status line, not a dead tile; the tile (and its
  // artifact link) appears once the report is actually saved.
  if (status.type === "running") {
    return (
      <ChatToolBase status={status}>
        {recipientName
          ? `Writing report for ${recipientName}`
          : "Writing report"}
      </ChatToolBase>
    );
  }

  // The report wasn't saved — the call errored, or the agent invented a
  // recipient outside the configured set — so don't imply one exists.
  const failedToSave =
    status.type === "incomplete" ||
    (status.type === "complete" && !savedArtifactId);
  if (failedToSave) {
    return (
      <ChatToolBase status={{ type: "incomplete" }}>
        {recipientName
          ? `Couldn't save report for ${recipientName}`
          : "Couldn't save report"}
      </ChatToolBase>
    );
  }

  const artifactHref =
    playbookId && savedArtifactId
      ? appPaths.playbookAutomationResult.build({
          playbookId,
          automationId,
          artifactId: savedArtifactId,
        })
      : undefined;

  const tile = (
    <AutomationReportTile
      title={title}
      subtitle={recipientName ? `For ${recipientName}` : undefined}
      isLinked={Boolean(artifactHref)}
    />
  );

  return artifactHref ? (
    <Link to={artifactHref} className="block w-full max-w-md">
      {tile}
    </Link>
  ) : (
    <div className="w-full max-w-md">{tile}</div>
  );
};

/**
 * The research thread's tool registry (research tools + the save_report tile).
 * A module-level constant — the components close over nothing, reading the run's
 * ambient values from `RunTranscriptContext`, so their identities never change
 * across renders. The default for any research-style thread (the score-movement
 * subagents, and the custom agent's own thread, which composes it with a notify
 * tool renderer).
 */
export const RESEARCH_THREAD_TOOLS: ChatToolComponents = {
  byName: {
    countCustomerInteractions: guardToolRender(CountCustomerInteractionsTool),
    researchCustomerInteractions: guardToolRender(
      ResearchCustomerInteractionsTool,
    ),
    getCriterionDetails: guardToolRender(GetCriterionDetailsTool),
    getPlaybookTeamScores: guardToolRender(GetPlaybookTeamScoresTool),
    getTeamRankings: guardToolRender(GetTeamRankingsTool),
    [SAVE_REPORT_TOOL_NAME]: guardToolRender(SaveReportTool),
  },
  Fallback: FallbackTool,
};

/**
 * If the tool call saved an artifact, returns the artifact id from its output so
 * the UI can link to it. The id is untrusted wire data, so it's validated
 * through the branded schema (a malformed id reads as "not saved").
 */
function getSavedArtifactId(output: unknown): AutomationArtifactId | null {
  if (output && typeof output === "object" && "artifact_id" in output) {
    const parsed = automationArtifactIdSchema.safeParse(output.artifact_id);
    if (parsed.success) {
      return parsed.data;
    }
  }
  return null;
}

/** Reads a tool call's `title` input, if present. */
function getReportTitle(input: unknown): string | undefined {
  if (input && typeof input === "object" && "title" in input) {
    const title = input.title;
    if (typeof title === "string" && title) {
      return title;
    }
  }
  return undefined;
}

/** Reads a tool call's `recipient_teammate_id` input, if present. */
function getRecipientTeammateId(input: unknown): string | undefined {
  if (input && typeof input === "object" && "recipient_teammate_id" in input) {
    const recipientId = input.recipient_teammate_id;
    if (typeof recipientId === "string" && recipientId) {
      return recipientId;
    }
  }
  return undefined;
}
